import QRCode from "qrcode";
import { GAME_URL } from "@/lib/share";

const base = `${process.env.PUBLIC_URL || ""}/game-assets`;

// Mirror of backend giftcard_points() — used only to preview an anonymous player's
// points before they register (the server recomputes on submit).
export function giftcardPoints(ch, { score = 0, beers = 0, deaths = 0, time_ms = 0 }) {
  const secs = Math.max(0, time_ms) / 1000;
  let pts = score + beers * (ch.beer_pts ?? 100);
  pts += Math.floor(Math.max(0, (ch.par_seconds ?? 120) - secs) * (ch.time_pts ?? 25));
  pts -= deaths * (ch.death_penalty ?? 500);
  return Math.max(0, Math.floor(pts));
}

export const fmtRunTime = (ms) => { const s = Math.max(0, Math.round((ms || 0) / 100) / 10); return `${Math.floor(s / 60)}:${(s % 60).toFixed(1).padStart(4, "0")}`; };

const loadImg = (src) => new Promise((resolve) => { const i = new Image(); i.crossOrigin = "anonymous"; i.onload = () => resolve(i); i.onerror = () => resolve(null); i.src = src; });

// Square 1080×1080 share card for a Gift Card Challenge run (WhatsApp/IG friendly).
export async function buildRunCardImage({ challenge, run, username = "", rank = null, es = true }) {
  const W = 1080, H = 1080;
  const c = document.createElement("canvas"); c.width = W; c.height = H;
  const x = c.getContext("2d");
  const g = x.createLinearGradient(0, 0, W, H); g.addColorStop(0, "#12294f"); g.addColorStop(1, "#0a1c38");
  x.fillStyle = g; x.fillRect(0, 0, W, H);
  x.strokeStyle = "#FFD54F"; x.lineWidth = 12; x.strokeRect(30, 30, W - 60, H - 60);
  x.textAlign = "center";

  const [nb, sp] = await Promise.all([loadImg(`${base}/nicabros-logo.webp`), loadImg(`${base}/${challenge.logo || "nicabros-logo.webp"}`)]);
  const ls = 170, ly = 60;
  x.fillStyle = "#fff";
  if (nb) { x.fillRect(200, ly, ls, ls); x.drawImage(nb, 210, ly + 10, ls - 20, ls - 20); }
  x.font = "bold 70px sans-serif"; x.fillText("×", W / 2, ly + ls / 2 + 24);
  if (sp) { x.fillStyle = "#fff"; x.fillRect(W - 200 - ls, ly, ls, ls); x.drawImage(sp, W - 190 - ls, ly + 10, ls - 20, ls - 20); }

  x.fillStyle = "#FFD54F"; x.font = "bold 54px sans-serif";
  x.fillText(es ? "RETO GIFT CARD $25" : "$25 GIFT CARD CHALLENGE", W / 2, ly + ls + 80);
  x.fillStyle = "rgba(255,255,255,0.85)"; x.font = "40px sans-serif";
  x.fillText(challenge.sponsor || challenge.name, W / 2, ly + ls + 135);

  x.fillStyle = "#fff"; x.font = "bold 60px sans-serif";
  x.fillText(username || (es ? "Mi corrida" : "My run"), W / 2, ly + ls + 225);
  x.fillStyle = "#FFD54F"; x.font = "bold 150px sans-serif";
  x.fillText(`${(run.points || 0).toLocaleString()}`, W / 2, ly + ls + 365);
  x.fillStyle = "rgba(255,255,255,0.8)"; x.font = "44px sans-serif";
  x.fillText(rank ? (es ? `PUNTOS · PUESTO #${rank}` : `POINTS · RANK #${rank}`) : (es ? "PUNTOS" : "POINTS"), W / 2, ly + ls + 450);

  const stats = [[es ? "TIEMPO" : "TIME", fmtRunTime(run.time_ms)], ["🍺", String(run.beers ?? 0)], [es ? "GOLPES" : "HITS", String(run.deaths ?? 0)], [es ? "PUNTAJE" : "SCORE", (run.score || 0).toLocaleString()]];
  const bw = 220, bh = 150, gap = 20, x0 = (W - (bw * 4 + gap * 3)) / 2, by = ly + ls + 500;
  stats.forEach(([lab, val], i) => {
    const bx = x0 + i * (bw + gap);
    x.fillStyle = "rgba(255,255,255,0.10)"; x.fillRect(bx, by, bw, bh);
    x.strokeStyle = "rgba(255,255,255,0.25)"; x.lineWidth = 3; x.strokeRect(bx, by, bw, bh);
    x.fillStyle = "rgba(255,255,255,0.6)"; x.font = "26px sans-serif"; x.fillText(lab, bx + bw / 2, by + 48);
    x.fillStyle = "#fff"; x.font = "bold 52px sans-serif"; x.fillText(val, bx + bw / 2, by + 115);
  });

  x.fillStyle = "#ff80ab"; x.font = "bold 34px sans-serif";
  x.fillText(es ? "MODO EXTREMO · ¿Me ganás?" : "EXTREME MODE · Can you beat me?", W / 2, H - 112);
  x.fillStyle = "rgba(255,255,255,0.6)"; x.font = "34px sans-serif";
  x.fillText(`nicabros.com/?gc=${challenge.id}`, W / 2, H - 62);
  return new Promise((resolve) => c.toBlob(resolve, "image/png"));
}

// Share the run card via the native share sheet (WhatsApp etc.); falls back to a download.
export async function shareRunCard({ challenge, run, username, rank, es }) {
  const blob = await buildRunCardImage({ challenge, run, username, rank, es });
  if (!blob) return "failed";
  const url = `${GAME_URL}/?gc=${encodeURIComponent(challenge.id)}`;
  const text = es
    ? `🏆 Hice ${(run.points || 0).toLocaleString()} pts en el Reto Gift Card de ${challenge.sponsor || challenge.name} (modo Extremo) en Nica Bros. ¿Me ganás? ${url}`
    : `🏆 I scored ${(run.points || 0).toLocaleString()} pts in the ${challenge.sponsor || challenge.name} Gift Card Challenge (Extreme) on Nica Bros. Can you beat me? ${url}`;
  const file = new File([blob], `nicabros-${challenge.id}-run.png`, { type: "image/png" });
  try {
    if (navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({ files: [file], text, title: "Nica Bros" });
      return "shared";
    }
  } catch (e) { if (e && e.name === "AbortError") return "cancelled"; }
  const a = document.createElement("a"); const u = URL.createObjectURL(blob);
  a.href = u; a.download = file.name; document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(u), 2000);
  try { await navigator.clipboard.writeText(text); } catch {}
  return "downloaded";
}

export const verifyUrl = (code) => `${GAME_URL}/?verify=${encodeURIComponent(code)}`;
export const qrDataUrl = (text) => QRCode.toDataURL(text, { width: 320, margin: 1, color: { dark: "#0a1c38", light: "#ffffff" } });
